import InfoIcon from '@mui/icons-material/Info'
import WarningIcon from '@mui/icons-material/Warning'
import {
  Alert,
  Box,
  Stack,
  Switch,
  Theme,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material'
import { ScheduleType } from '../../shared/types'
import './payout-method-infobox.scss'

/**
 * Written by Windsurf using model: Claude Sonnet 4.5 (Thinking)
 */
interface PayoutMethodInfoboxProps {
  readonly scheduleType: ScheduleType
  readonly onScheduleTypeChange: (scheduleType: ScheduleType) => void
  readonly visible?: boolean
  readonly showRoundingWarning?: boolean
  readonly showExceeds100Warning?: boolean
  readonly originalScheduleType?: ScheduleType
  readonly hasDollarAmount?: boolean
  readonly disableToggle?: boolean
  readonly disableToggleMessage?: string
}

/**
 * Written by Windsurf using model: Claude Sonnet 4.5 (Thinking)
 */
export default function PayoutMethodInfobox({
  scheduleType,
  onScheduleTypeChange,
  visible = true,
  showRoundingWarning = false,
  showExceeds100Warning = false,
  originalScheduleType,
  hasDollarAmount = false,
  disableToggle = false,
  disableToggleMessage,
}: Readonly<PayoutMethodInfoboxProps>) {
  const theme: Theme = useTheme()

  // Don't render if not visible (when feature flag is disabled)
  if (!visible) {
    return null
  }

  const isBookValue = scheduleType === ScheduleType.BOOK_VALUE

  const typeLabel = isBookValue ? 'Book Value' : 'Deposit'

  const mainText = isBookValue
    ? "Percentages are applied to the Contract's Book Value at time of payment. The final payment must always be 100%."
    : 'Percentages are referenced against the Deposit amount. All percentages must sum to 100%.'

  const tooltipText =
    "This determines how partial maturities are defined in all scenarios. Deposit indicates the percentage is referenced against the Deposit amount, and percentages should sum to 100%. Book Value indicates that the percentage is applied to the Contract's Book Value at time of payment, so final payment should always be 100%."

  const conversionText =
    'Toggling Payment Reference may result in small rounding differences. Be sure to review and validate.'

  return (
    <>
      <Alert
        severity="info"
        icon={<InfoIcon sx={{ color: theme.palette.primary.main }} />}
        className="payout-method-infobox"
        sx={{ backgroundColor: theme.palette.primary.lightest }}
      >
        <Box className="infobox-content">
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
              Payment Reference
            </Typography>
          </Box>

          <Tooltip title={tooltipText} enterDelay={500}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0,
                width: 'fit-content',
              }}
            >
              <Typography
                variant="body2"
                onClick={() =>
                  !disableToggle && onScheduleTypeChange(ScheduleType.DEPOSIT)
                }
                sx={{
                  cursor: disableToggle ? 'default' : 'pointer',
                  userSelect: 'none',
                  opacity: disableToggle ? 0.5 : 1,
                  '&:hover': { opacity: disableToggle ? 0.5 : 0.7 },
                }}
              >
                Deposit
              </Typography>
              <Switch
                checked={scheduleType === ScheduleType.BOOK_VALUE}
                disabled={disableToggle}
                onChange={(e) =>
                  onScheduleTypeChange(
                    e.target.checked
                      ? ScheduleType.BOOK_VALUE
                      : ScheduleType.DEPOSIT
                  )
                }
              />
              <Typography
                variant="body2"
                onClick={() =>
                  !disableToggle &&
                  onScheduleTypeChange(ScheduleType.BOOK_VALUE)
                }
                sx={{
                  cursor: disableToggle ? 'default' : 'pointer',
                  userSelect: 'none',
                  opacity: disableToggle ? 0.5 : 1,
                  '&:hover': { opacity: disableToggle ? 0.5 : 0.7 },
                }}
              >
                Book Value
              </Typography>
            </Box>
          </Tooltip>

          {hasDollarAmount && (
            <Typography
              variant="body2"
              sx={{
                backgroundColor: 'rgba(0, 98, 196, 0.12)',
                borderLeft: '1px solid #0062C4',
                borderRadius: '4px',
                padding: '8px 12px',
                color: '#333333',
              }}
            >
              If toggling Payment Reference, please review any scenarios
              containing a dollar-based payment as they may not convert as
              expected.
            </Typography>
          )}

          {disableToggle && disableToggleMessage != null && (
            <Typography
              variant="body2"
              sx={{
                backgroundColor: 'rgba(0, 98, 196, 0.12)',
                borderLeft: '1px solid #0062C4',
                borderRadius: '4px',
                padding: '8px 12px',
                color: '#333333',
              }}
            >
              {disableToggleMessage}
            </Typography>
          )}

          <Stack spacing={1}>
            <Typography variant="body2">
              <Box
                component="span"
                sx={{ fontWeight: 'bold', color: 'text.primary' }}
              >
                {typeLabel}:
              </Box>{' '}
              <Box component="span" sx={{ color: theme.palette.primary.dark }}>
                {mainText}
              </Box>
            </Typography>
            {originalScheduleType === undefined ? (
              <Typography
                variant="body2"
                sx={{ color: theme.palette.warning.dark }}
              >
                This method applies to all scenarios in this proposal.
              </Typography>
            ) : (
              <Typography
                variant="body2"
                sx={{ color: theme.palette.warning.dark }}
              >
                This proposal was created using{' '}
                <Box component="span" sx={{ fontWeight: 'bold' }}>
                  {originalScheduleType === ScheduleType.BOOK_VALUE
                    ? 'Book Value'
                    : 'Deposit'}
                </Box>{' '}
                percentages. You can toggle to view in either format.
              </Typography>
            )}
          </Stack>
        </Box>
      </Alert>

      {showRoundingWarning && (
        <Alert severity="warning" icon={<WarningIcon />} sx={{ mb: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
            Conversion Warning
          </Typography>
          <Typography variant="body2">{conversionText}</Typography>
        </Alert>
      )}

      {showExceeds100Warning && (
        <Alert severity="warning" icon={<WarningIcon />} sx={{ mb: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
            Total Exceeds 100%
          </Typography>
          <Typography variant="body2">
            Switching to Deposit mode will restore previous values that sum to
            more than 100%. Please review and adjust the payment schedule.
          </Typography>
        </Alert>
      )}
    </>
  )
}
